import { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw, Check, X, Trash2, ChevronRight, FileText, Lightbulb, Target, ListTree, Ban, ArrowUpRight, Lock, Edit3, Send, Inbox, Database, Award, Gift, Plus } from "lucide-react";
import ProgressLoader from "@/components/progress-loader";

const BASE = import.meta.env.BASE_URL;

function authHeaders() {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

const statusColor: Record<string, string> = {
  draft: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  reviewed: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  promoted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  dismissed: "bg-white/5 text-white/40 border-white/10",
};
const impactColor: Record<string, string> = {
  high: "text-red-400 border-red-500/30 bg-red-500/10",
  medium: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  low: "text-white/50 border-white/10 bg-white/5",
};
const categoryColor: Record<string, string> = {
  ux: "text-purple-400", growth: "text-emerald-400", infra: "text-blue-400", data: "text-cyan-400", agents: "text-[#FF5C00]", trust: "text-amber-400",
};

type TopTab = "prds" | "value_props" | "suggestions" | "rewards";

export default function AdminPrds() {
  const [tab, setTab] = useState<TopTab>("prds");
  const [pendingSuggestions, setPendingSuggestions] = useState(0);

  const loadPending = async () => {
    try {
      const r = await fetch(`${BASE}api/admin/suggestions?status=submitted`, { headers: authHeaders() });
      if (r.ok) { const j = await r.json(); setPendingSuggestions(j.pendingCount ?? 0); }
    } catch { /* badge is best-effort */ }
  };
  useEffect(() => { loadPending(); }, []);

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5 -mx-2 md:mx-0">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/30 to-[#FF5C00]/30 border border-purple-500/30 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-purple-300" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white">PC · Product Chief</h1>
          <p className="text-white/55 text-sm mt-1">Reads platform signals, writes PRDs, and translates every shipped feature into honest value props per audience. You decide what ships and what gets said.</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-white/[0.08] flex-wrap">
        <TabButton active={tab === "prds"} onClick={() => setTab("prds")} icon={<FileText className="w-4 h-4" />}>PRDs</TabButton>
        <TabButton active={tab === "value_props"} onClick={() => setTab("value_props")} icon={<Target className="w-4 h-4" />}>Value Propositions</TabButton>
        <TabButton active={tab === "suggestions"} onClick={() => setTab("suggestions")} icon={<Inbox className="w-4 h-4" />} badge={pendingSuggestions}>Suggestions</TabButton>
        <TabButton active={tab === "rewards"} onClick={() => setTab("rewards")} icon={<Gift className="w-4 h-4" />}>Rewards</TabButton>
      </div>

      {tab === "prds" && <PrdsTab />}
      {tab === "value_props" && <ValuePropsTab />}
      {tab === "suggestions" && <SuggestionsTab onReviewed={loadPending} />}
      {tab === "rewards" && <RewardsTab />}
    </div>
  );
}

function TabButton({ active, onClick, icon, children, badge }: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode; badge?: number }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${active ? "text-white border-[#FF5C00]" : "text-white/55 hover:text-white/80 border-transparent"}`}>
      {icon}{children}
      {badge !== undefined && badge > 0 && (
        <span className="ml-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[#FF5C00] text-white leading-none">{badge}</span>
      )}
    </button>
  );
}

// =============================================================================
// PRDs Tab — original behaviour, refactored into a sub-component
// =============================================================================

function PrdsTab() {
  const [prds, setPrds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState<any | null>(null);
  const [genResult, setGenResult] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = async () => {
    setLoadError(null);
    try {
      const r = await fetch(`${BASE}api/admin/prds`, { headers: authHeaders() });
      if (r.status === 401) { setLoadError("Your admin session expired. Please log in again."); }
      else if (r.ok) { setPrds(await r.json()); }
      else { setLoadError(`Failed to load PRDs (${r.status}).`); }
    } catch { setLoadError("Network error loading PRDs."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true); setGenResult(null);
    try {
      const r = await fetch(`${BASE}api/admin/prds/generate`, { method: "POST", headers: authHeaders() });
      if (r.ok) { const j = await r.json(); setGenResult(j.created > 0 ? `PC drafted ${j.created} new PRD${j.created === 1 ? "" : "s"}.` : "PC reviewed signals — nothing worth proposing right now."); await load(); }
      else { const e = await r.json(); setGenResult(e.error || "Favcy Navigator is not responding right now. This is usually a transient issue — please try again after 10 mins."); }
    } catch { setGenResult("Network error"); }
    setGenerating(false);
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      const r = await fetch(`${BASE}api/admin/prds/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status }) });
      if (r.ok) { const updated = await r.json(); setPrds(p => p.map(x => x.id === id ? { ...x, ...updated } : x)); if (selected?.id === id) setSelected({ ...selected, ...updated }); }
    } catch {}
  };

  const remove = async (id: number) => {
    if (!confirm("Delete this PRD permanently?")) return;
    try {
      const r = await fetch(`${BASE}api/admin/prds/${id}`, { method: "DELETE", headers: authHeaders() });
      if (r.ok) { setPrds(p => p.filter(x => x.id !== id)); if (selected?.id === id) setSelected(null); }
    } catch {}
  };

  const filtered = prds.filter(p => filter === "all" || p.status === filter);
  const counts = { draft: prds.filter(p => p.status === "draft").length, reviewed: prds.filter(p => p.status === "reviewed").length, promoted: prds.filter(p => p.status === "promoted").length, dismissed: prds.filter(p => p.status === "dismissed").length };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <button onClick={generate} disabled={generating} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> PC is thinking...</> : <><RefreshCw className="w-4 h-4" /> Generate PRDs now</>}
        </button>
      </div>

      {genResult && <div className="bg-white/[0.04] border border-[#FF5C00]/25 rounded-lg p-3 text-sm text-white/85 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF5C00]" /> {genResult}</div>}
      {loadError && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">{loadError}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          { k: "draft", label: "Drafts", count: counts.draft },
          { k: "reviewed", label: "Reviewed", count: counts.reviewed },
          { k: "promoted", label: "Promoted", count: counts.promoted },
          { k: "dismissed", label: "Dismissed", count: counts.dismissed },
        ].map(t => (
          <button key={t.k} onClick={() => setFilter(filter === t.k ? "all" : t.k)} className={`text-left rounded-xl border p-3.5 transition-all ${filter === t.k ? "ring-2 ring-[#FF5C00]/40 border-[#FF5C00]/40" : "border-white/[0.08] hover:border-white/[0.2]"} bg-white/[0.04]`}>
            <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{t.label}</p>
            <p className="text-2xl font-bold text-white mt-1">{t.count}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2 max-h-[700px] overflow-y-auto pr-1">
          {loading ? (
            <>{[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-white/[0.04] rounded-xl animate-pulse" />)}</>
          ) : filtered.length === 0 ? (
            <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-8 text-center">
              <FileText className="w-8 h-8 text-white/20 mx-auto mb-2" />
              <p className="text-white/55 text-sm font-medium">{prds.length === 0 ? "No PRDs yet." : "Nothing in this filter."}</p>
              <p className="text-white/40 text-xs mt-1">{prds.length === 0 ? "PC runs at 10 AM IST daily, or hit Generate now." : "Try clearing the filter."}</p>
            </div>
          ) : (
            filtered.map(p => (
              <button key={p.id} onClick={() => setSelected(p)} className={`w-full text-left bg-white/[0.04] hover:bg-white/[0.07] rounded-xl p-4 border transition-colors ${selected?.id === p.id ? "border-[#FF5C00]/50" : "border-white/[0.08]"}`}>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${statusColor[p.status]}`}>{p.status}</span>
                  <span className={`text-[9px] uppercase tracking-wider font-mono ${categoryColor[p.category] || "text-white/50"}`}>{p.category}</span>
                </div>
                <p className="text-white text-sm font-semibold leading-tight">{p.title}</p>
                {p.oneLiner && <p className="text-white/55 text-xs mt-1.5 line-clamp-2 leading-relaxed">{p.oneLiner}</p>}
                <div className="flex items-center gap-1.5 mt-2 text-[10px] font-mono">
                  <span className={`px-1.5 py-0.5 rounded border ${impactColor[p.impact]}`}>{p.impact} impact</span>
                  <span className="text-white/30">· {p.effort} effort</span>
                  <span className="text-white/30 ml-auto">{new Date(p.createdAt).toLocaleDateString()}</span>
                </div>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-12 text-center">
              <Lightbulb className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/55 text-sm font-medium">Pick a PRD to read it in full.</p>
              <p className="text-white/40 text-xs mt-1">Each PRD is grounded in real platform signals from the last week.</p>
            </div>
          ) : (
            <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6 space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${statusColor[selected.status]}`}>{selected.status}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-mono ${categoryColor[selected.category] || "text-white/50"}`}>{selected.category}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${impactColor[selected.impact]}`}>{selected.impact} impact · {selected.effort} effort</span>
                </div>
                <h2 className="text-2xl font-bold text-white">{selected.title}</h2>
                {selected.oneLiner && <p className="text-white/65 text-sm mt-1.5 italic">{selected.oneLiner}</p>}
                <p className="text-white/35 text-[10px] mt-2 font-mono">Drafted by {selected.agentName} · {new Date(selected.createdAt).toLocaleString()}</p>
              </div>

              <Section icon={<Target className="w-4 h-4" />} title="Problem">
                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{selected.problem}</p>
              </Section>

              {Array.isArray(selected.evidence) && selected.evidence.length > 0 && (
                <Section icon={<FileText className="w-4 h-4" />} title="Evidence">
                  <ul className="space-y-1.5 text-sm text-white/80">
                    {selected.evidence.map((ev: string, i: number) => (
                      <li key={i} className="flex gap-2"><span className="text-[#FF5C00] mt-0.5">›</span><span className="leading-relaxed">{ev}</span></li>
                    ))}
                  </ul>
                </Section>
              )}

              <Section icon={<Lightbulb className="w-4 h-4" />} title="Proposal">
                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{selected.proposal}</p>
              </Section>

              {selected.successMetrics && <Section icon={<ArrowUpRight className="w-4 h-4" />} title="Success metrics"><p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{selected.successMetrics}</p></Section>}
              {selected.scope && <Section icon={<ListTree className="w-4 h-4" />} title="In scope"><p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{selected.scope}</p></Section>}
              {selected.outOfScope && <Section icon={<Ban className="w-4 h-4" />} title="Out of scope"><p className="text-white/65 text-sm leading-relaxed whitespace-pre-wrap">{selected.outOfScope}</p></Section>}

              <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-white/[0.08]">
                {selected.status !== "promoted" && (
                  <button onClick={() => updateStatus(selected.id, "promoted")} className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-medium"><Check className="w-3.5 h-3.5" /> Promote to roadmap</button>
                )}
                {selected.status === "draft" && (
                  <button onClick={() => updateStatus(selected.id, "reviewed")} className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5" /> Mark reviewed</button>
                )}
                {selected.status !== "dismissed" && (
                  <button onClick={() => updateStatus(selected.id, "dismissed")} className="bg-white/[0.06] hover:bg-white/[0.1] text-white/70 border border-white/15 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Dismiss</button>
                )}
                <button onClick={() => remove(selected.id)} className="ml-auto text-red-400 hover:text-red-300 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Value Propositions Tab
// =============================================================================

const moscowColor: Record<string, string> = {
  must: "bg-red-500/15 text-red-300 border-red-500/30",
  should: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  could: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  wont: "bg-white/5 text-white/40 border-white/10",
};
const moscowLabel: Record<string, string> = { must: "Must", should: "Should", could: "Could", wont: "Won't" };

type Audience = "admin" | "advisor" | "investor";

function ValuePropsTab() {
  const [audience, setAudience] = useState<Audience>("admin");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [actionResult, setActionResult] = useState<string | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [intakeCount, setIntakeCount] = useState<number>(0);
  const [showIntakeForm, setShowIntakeForm] = useState(false);

  const load = async (aud: Audience) => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}api/admin/pc/value-props?audience=${aud}`, { headers: authHeaders() });
      if (r.ok) { const j = await r.json(); setItems(j.items || []); }
      else setItems([]);
    } catch { setItems([]); }
    setLoading(false);
  };

  const loadIntakeCount = async () => {
    try {
      const r = await fetch(`${BASE}api/admin/pc/intake?status=pending`, { headers: authHeaders() });
      if (r.ok) { const j = await r.json(); setIntakeCount(j.items?.length || 0); }
    } catch {}
  };

  useEffect(() => { load(audience); }, [audience]);
  useEffect(() => { loadIntakeCount(); }, []);

  const runNow = async () => {
    setRunning(true); setActionResult(null);
    try {
      const r = await fetch(`${BASE}api/admin/pc/run-now`, { method: "POST", headers: authHeaders() });
      const j = await r.json();
      if (r.ok) { setActionResult(`PC ran: drained ${j.intakeProcessed} intake, wrote ${j.valuePropsWritten} value props (${j.valuePropsSkipped} already covered).`); await load(audience); await loadIntakeCount(); }
      else setActionResult(j.error || "Run failed");
    } catch { setActionResult("Network error"); }
    setRunning(false);
  };

  const backfill = async () => {
    if (!confirm("Backfill writes a value prop for every (feature × audience) pair that's missing one. This calls the model many times — proceed?")) return;
    setBackfilling(true); setActionResult(null);
    try {
      const r = await fetch(`${BASE}api/admin/pc/value-props/backfill`, { method: "POST", headers: authHeaders() });
      const j = await r.json();
      if (r.ok) { setActionResult(`Backfill complete: ${j.written} new entries, ${j.skipped} skipped (${j.total} total checked).`); await load(audience); }
      else setActionResult(j.error || "Backfill failed");
    } catch { setActionResult("Network error"); }
    setBackfilling(false);
  };

  const regen = async (id: number) => {
    setActionResult(null);
    try {
      const r = await fetch(`${BASE}api/admin/pc/value-props/${id}/regenerate`, { method: "POST", headers: authHeaders() });
      if (r.ok) { setActionResult("Regenerated."); await load(audience); }
      else { const e = await r.json(); setActionResult(e.error || "Regenerate failed"); }
    } catch { setActionResult("Network error"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 bg-white/[0.04] border border-white/[0.08] rounded-lg p-1">
          {(["admin", "advisor", "investor"] as Audience[]).map(a => (
            <button key={a} onClick={() => setAudience(a)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono rounded-md transition-colors ${audience === a ? "bg-[#FF5C00] text-white" : "text-white/60 hover:text-white"}`}>{a}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {intakeCount > 0 && <span className="text-[11px] font-mono px-2 py-1 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300 flex items-center gap-1.5"><Inbox className="w-3 h-3" /> {intakeCount} pending intake</span>}
          <button onClick={() => setShowIntakeForm(s => !s)} className="bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/15 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Submit feature</button>
          <button onClick={runNow} disabled={running} className="bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/15 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50">{running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Run now</button>
          <button onClick={backfill} disabled={backfilling} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Database className="w-3.5 h-3.5" />{backfilling ? "Backfilling..." : "Backfill all"}</button>
        </div>
      </div>

      {showIntakeForm && <IntakeForm onSubmitted={() => { setShowIntakeForm(false); loadIntakeCount(); }} />}

      {actionResult && <div className="bg-white/[0.04] border border-[#FF5C00]/25 rounded-lg p-3 text-sm text-white/85 flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#FF5C00]" /> {actionResult}</div>}

      <div className="space-y-2">
        {loading ? (
          <>{[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white/[0.04] rounded-xl animate-pulse" />)}</>
        ) : items.length === 0 ? (
          <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-10 text-center">
            <Target className="w-8 h-8 text-white/20 mx-auto mb-2" />
            <p className="text-white/55 text-sm font-medium">No value props for the {audience} audience yet.</p>
            <p className="text-white/40 text-xs mt-1.5">Hit "Backfill all" to generate them for every feature in the catalog.</p>
          </div>
        ) : (
          items.map((vp) => (
            <div key={vp.id} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-4">
              <div className="flex items-start gap-3 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${moscowColor[vp.moscow] || moscowColor.could}`}>{moscowLabel[vp.moscow] || vp.moscow}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white text-sm font-semibold">{vp.featureName}</h3>
                    {vp.locked && <span className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> admin-edited</span>}
                    {vp.featureCategory && <span className="text-[10px] uppercase tracking-wider font-mono text-white/40">· {vp.featureCategory}</span>}
                  </div>
                  <p className="text-white/80 text-sm mt-2 leading-relaxed">{vp.valueProp}</p>
                  <p className="text-white/55 text-xs mt-1.5 italic">Benefit: {vp.benefit}</p>
                  {vp.featureWhereInApp && <p className="text-white/35 text-[11px] mt-2 font-mono">→ {vp.featureWhereInApp}</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setEditing(vp)} className="text-white/55 hover:text-white text-xs px-2 py-1 rounded border border-white/15 hover:border-white/30 flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit</button>
                  <button onClick={() => regen(vp.id)} className="text-white/55 hover:text-white text-xs px-2 py-1 rounded border border-white/15 hover:border-white/30 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Regen</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editing && <EditModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(audience); }} />}
    </div>
  );
}

function IntakeForm({ onSubmitted }: { onSubmitted: () => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [whereInApp, setWhereInApp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (title.trim().length < 3) { setError("Title needs at least 3 characters."); return; }
    if (description.trim().length < 10) { setError("Describe what shipped in at least 10 characters."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${BASE}api/admin/pc/intake`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ title: title.trim(), description: description.trim(), whereInApp: whereInApp.trim() || null, processNow: true }) });
      const j = await r.json();
      if (r.ok) { setTitle(""); setDescription(""); setWhereInApp(""); onSubmitted(); }
      else setError(j.error || "Submit failed");
    } catch { setError("Network error"); }
    setSubmitting(false);
  };

  return (
    <div className="bg-white/[0.04] border border-[#FF5C00]/25 rounded-xl p-4 space-y-3">
      <p className="text-white/85 text-sm font-medium">Tell PC what shipped</p>
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Feature title (e.g. 'Banker artifact suggestions')" className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
      <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Plain English: what does it do, who uses it, what changes for them?" rows={4} className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
      <input value={whereInApp} onChange={e => setWhereInApp(e.target.value)} placeholder="Where in app (optional, e.g. /banker/tasks)" className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
      {error && <p className="text-red-300 text-xs">{error}</p>}
      <div className="flex items-center gap-2">
        <button onClick={submit} disabled={submitting} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5">{submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Submit to PC</button>
        <p className="text-white/40 text-xs">PC will normalise it into the catalog and then write per-audience value props.</p>
      </div>
    </div>
  );
}

function EditModal({ item, onClose, onSaved }: { item: any; onClose: () => void; onSaved: () => void }) {
  const [valueProp, setValueProp] = useState(item.valueProp || "");
  const [benefit, setBenefit] = useState(item.benefit || "");
  const [moscow, setMoscow] = useState(item.moscow || "could");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null); setSaving(true);
    try {
      const r = await fetch(`${BASE}api/admin/pc/value-props/${item.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ valueProp, benefit, moscow }) });
      if (r.ok) onSaved();
      else { const e = await r.json(); setError(e.error || "Save failed"); }
    } catch { setError("Network error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/[0.15] rounded-2xl p-6 max-w-2xl w-full space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-white text-lg font-bold">{item.featureName}</h3>
            <p className="text-white/55 text-xs mt-0.5 font-mono">audience: {item.audience} · saving will lock this entry from auto-overwrites</p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div>
          <label className="text-white/55 text-[10px] uppercase tracking-wider font-mono">Value proposition</label>
          <textarea value={valueProp} onChange={e => setValueProp(e.target.value)} rows={3} className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white mt-1" />
        </div>
        <div>
          <label className="text-white/55 text-[10px] uppercase tracking-wider font-mono">Concrete benefit</label>
          <textarea value={benefit} onChange={e => setBenefit(e.target.value)} rows={2} className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white mt-1" />
        </div>
        <div>
          <label className="text-white/55 text-[10px] uppercase tracking-wider font-mono">MoSCoW priority for this audience</label>
          <select value={moscow} onChange={e => setMoscow(e.target.value)} className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white mt-1">
            <option value="must">Must — they cannot do their job well without this</option>
            <option value="should">Should — meaningfully improves their week</option>
            <option value="could">Could — useful in specific moments</option>
            <option value="wont">Won't — not relevant to this audience</option>
          </select>
        </div>
        {error && <p className="text-red-300 text-xs">{error}</p>}
        <div className="flex items-center gap-2 justify-end pt-2 border-t border-white/[0.08]">
          <button onClick={onClose} className="text-white/70 text-sm px-3 py-1.5 rounded-lg border border-white/15 hover:bg-white/[0.05]">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />} Save & lock</button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-white/50 text-[10px] uppercase tracking-wider font-mono font-semibold mb-2 flex items-center gap-1.5">{icon} {title}</p>
      {children}
    </div>
  );
}

// =============================================================================
// Suggestions Tab — cross-surface improvement review (PRD §4, §7.2)
// =============================================================================

const sugStatusColor: Record<string, string> = {
  submitted: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  accepted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  declined: "bg-white/5 text-white/40 border-white/10",
  needs_info: "bg-amber-500/15 text-amber-400 border-amber-500/30",
};
const kindColor: Record<string, string> = {
  founder: "text-emerald-400", investor: "text-blue-400", advisor: "text-purple-400", mentor: "text-cyan-400", admin: "text-[#FF5C00]",
};
const SUG_STATUSES = ["submitted", "needs_info", "accepted", "declined"] as const;

function SuggestionsTab({ onReviewed }: { onReviewed: () => void }) {
  const [status, setStatus] = useState<string>("submitted");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any | null>(null);
  const [impact, setImpact] = useState<"low" | "medium" | "high">("medium");
  const [reviewNote, setReviewNote] = useState("");
  const [promote, setPromote] = useState(false);
  const [busy, setBusy] = useState<null | "accept" | "decline" | "needs_info">(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const load = async (st: string) => {
    setLoading(true); setError(null); setWarning(null);
    try {
      const r = await fetch(`${BASE}api/admin/suggestions?status=${st}`, { headers: authHeaders() });
      if (r.status === 401) setError("Your admin session expired. Please log in again.");
      else if (r.ok) { const j = await r.json(); setItems(j.suggestions || []); }
      else setError(`Failed to load suggestions (${r.status}).`);
    } catch { setError("Network error loading suggestions."); }
    setLoading(false);
  };
  useEffect(() => { load(status); setSelected(null); }, [status]);

  const pick = (s: any) => {
    setSelected(s); setError(null);
    setImpact("medium"); setReviewNote(s.reviewNote || ""); setPromote(false);
  };

  const review = async (action: "accept" | "decline" | "needs_info") => {
    if (!selected) return;
    setBusy(action); setError(null); setWarning(null);
    try {
      const body: any = { action, reviewNote: reviewNote.trim() || null };
      if (action === "accept") { body.impact = impact; body.promote = promote; }
      const r = await fetch(`${BASE}api/admin/suggestions/${selected.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => null);
      if (r.ok) {
        // Acceptance succeeded, but best-effort legs (email/bell/promote) may
        // have failed — surface them so PC knows to retry, not silently drop.
        const legErrors: string[] = j?.result?.errors ?? [];
        await load(status);
        if (legErrors.length) {
          setSelected(null);
          setWarning(`Accepted, but some follow-up steps failed: ${legErrors.join("; ")}. Re-run Accept to retry just the missing step.`);
        } else {
          setSelected(null);
        }
        onReviewed();
      } else {
        setError(j?.error || `Review failed (${r.status}).`);
      }
    } catch { setError("Network error during review."); }
    setBusy(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 bg-white/[0.04] border border-white/[0.08] rounded-lg p-1 w-fit">
        {SUG_STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono rounded-md transition-colors ${status === s ? "bg-[#FF5C00] text-white" : "text-white/60 hover:text-white"}`}>{s.replace(/_/g, " ")}</button>
        ))}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300 flex items-center gap-2"><X className="w-4 h-4" /> {error}</div>}
      {warning && <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-300 flex items-start gap-2"><X className="w-4 h-4 mt-0.5 shrink-0" /> <span>{warning}</span></div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2 lg:col-span-1">
          {loading ? (
            <>{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-white/[0.04] rounded-xl animate-pulse" />)}</>
          ) : items.length === 0 ? (
            <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-8 text-center">
              <Inbox className="w-7 h-7 text-white/20 mx-auto mb-2" />
              <p className="text-white/55 text-sm font-medium">No {status.replace(/_/g, " ")} suggestions.</p>
            </div>
          ) : (
            items.map((s) => (
              <button key={s.id} onClick={() => pick(s)} className={`w-full text-left bg-white/[0.04] border rounded-xl p-3 transition-colors ${selected?.id === s.id ? "border-[#FF5C00]/60" : "border-white/[0.1] hover:border-white/25"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] uppercase tracking-wider font-mono ${kindColor[s.submitterKind] || "text-white/50"}`}>{s.submitterKind}</span>
                  <span className="text-white/30 text-[10px] font-mono">· {s.subCategory}</span>
                  <span className="text-white/30 text-[10px] ml-auto">{new Date(s.createdAt).toLocaleDateString()}</span>
                </div>
                <p className="text-white text-sm font-medium truncate">{s.title}</p>
              </button>
            ))
          )}
        </div>

        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-12 text-center">
              <Lightbulb className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/55 text-sm font-medium">Pick a suggestion to review it.</p>
              <p className="text-white/40 text-xs mt-1">Accepting awards bounty points to the submitter and emails them.</p>
            </div>
          ) : (
            <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-6 space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${sugStatusColor[selected.status] || "text-white/50 border-white/10"}`}>{selected.status.replace(/_/g, " ")}</span>
                  <span className={`text-[10px] uppercase tracking-wider font-mono ${kindColor[selected.submitterKind] || "text-white/50"}`}>{selected.submitterKind}</span>
                  <span className="text-[10px] uppercase tracking-wider font-mono text-white/40">· {selected.type} · {selected.subCategory}</span>
                </div>
                <h2 className="text-2xl font-bold text-white">{selected.title}</h2>
                <p className="text-white/35 text-[10px] mt-2 font-mono">From {selected.submitterEmail} · {new Date(selected.createdAt).toLocaleString()}{selected.surfacePath ? ` · ${selected.surfacePath}` : ""}</p>
              </div>

              <Section icon={<FileText className="w-4 h-4" />} title="Details">
                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap">{selected.bodyMd}</p>
              </Section>

              {selected.screenshotUrl && (
                <Section icon={<ArrowUpRight className="w-4 h-4" />} title="Screenshot">
                  <a href={selected.screenshotUrl} target="_blank" rel="noreferrer" className="text-[#FF5C00] text-sm hover:underline break-all">{selected.screenshotUrl}</a>
                </Section>
              )}

              {selected.reviewNote && selected.status !== "submitted" && (
                <Section icon={<ChevronRight className="w-4 h-4" />} title="Review note">
                  <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap">{selected.reviewNote}</p>
                </Section>
              )}

              {(selected.status === "submitted" || selected.status === "needs_info") && (
                <div className="space-y-3 pt-2 border-t border-white/[0.08]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white/55 text-[10px] uppercase tracking-wider font-mono">Impact</span>
                      <select value={impact} onChange={(e) => setImpact(e.target.value as any)} className="bg-black/30 border border-white/15 rounded-lg px-2 py-1 text-sm text-white">
                        <option value="low">Low (+ points)</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <label className="flex items-center gap-1.5 text-white/70 text-xs cursor-pointer">
                      <input type="checkbox" checked={promote} onChange={(e) => setPromote(e.target.checked)} className="accent-[#FF5C00]" />
                      Promote to a PRD on accept
                    </label>
                  </div>
                  <textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder="Optional note to the submitter (shown in their My Bounties)." rows={3} className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />

                  {busy === "accept" ? (
                    <ProgressLoader active title="Accepting & awarding bounty" stages={["Crediting bounty points", "Flagging the suggestion accepted", promote ? "Promoting to a PRD" : "Emailing the submitter", "Emailing the submitter"]} stageIntervalMs={1100} />
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => review("accept")} disabled={busy !== null} className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-medium"><Check className="w-3.5 h-3.5" /> Accept & award</button>
                      <button onClick={() => review("needs_info")} disabled={busy !== null} className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5">{busy === "needs_info" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />} Needs info</button>
                      <button onClick={() => review("decline")} disabled={busy !== null} className="bg-white/[0.06] hover:bg-white/[0.1] text-white/70 border border-white/15 text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5">{busy === "decline" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Decline</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Rewards Tab — redemption offers + requests management (PRD §7.3, §8)
// =============================================================================

const offerStatusColor: Record<string, string> = {
  draft: "bg-white/5 text-white/40 border-white/10",
  open: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  closed: "bg-white/5 text-white/40 border-white/10",
};
const reqStatusColor: Record<string, string> = {
  requested: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  fulfilled: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-white/5 text-white/40 border-white/10",
};

function RewardsTab() {
  const [offers, setOffers] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [reqStatus, setReqStatus] = useState<string>("requested");
  const [resolvingId, setResolvingId] = useState<number | null>(null);

  const loadOffers = async () => {
    try {
      const r = await fetch(`${BASE}api/admin/redemption-offers`, { headers: authHeaders() });
      if (r.ok) { const j = await r.json(); setOffers(j.offers || []); }
      else if (r.status === 401) setError("Your admin session expired. Please log in again.");
    } catch { setError("Network error loading offers."); }
  };
  const loadRequests = async (st: string) => {
    try {
      const r = await fetch(`${BASE}api/admin/redemption-requests?status=${st}`, { headers: authHeaders() });
      if (r.ok) { const j = await r.json(); setRequests(j.requests || []); }
    } catch { /* non-fatal */ }
  };

  useEffect(() => { (async () => { setLoading(true); setError(null); await Promise.all([loadOffers(), loadRequests(reqStatus)]); setLoading(false); })(); }, []);
  useEffect(() => { loadRequests(reqStatus); }, [reqStatus]);

  const toggleOffer = async (offer: any) => {
    const next = offer.status === "open" ? "closed" : "open";
    try {
      const r = await fetch(`${BASE}api/admin/redemption-offers/${offer.id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ status: next }) });
      if (r.ok) await loadOffers();
    } catch { /* non-fatal */ }
  };

  const resolve = async (id: number, action: "fulfil" | "reject") => {
    const note = action === "reject" ? (prompt("Reason for rejection (returned to the requester):") ?? "") : "";
    setResolvingId(id); setError(null);
    try {
      const r = await fetch(`${BASE}api/admin/redemption-requests/${id}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ action, adminNote: note || null }) });
      const j = await r.json().catch(() => null);
      if (r.ok) { await Promise.all([loadRequests(reqStatus), loadOffers()]); }
      else setError(j?.error || `Action failed (${r.status}).`);
    } catch { setError("Network error resolving request."); }
    setResolvingId(null);
  };

  return (
    <div className="space-y-6">
      {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300 flex items-center gap-2"><X className="w-4 h-4" /> {error}</div>}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-white/85 text-sm font-semibold flex items-center gap-2"><Gift className="w-4 h-4 text-[#FF5C00]" /> Redemption offers</p>
          <button onClick={() => setShowCreate((s) => !s)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New offer</button>
        </div>

        {showCreate && <OfferForm onCreated={() => { setShowCreate(false); loadOffers(); }} />}

        {loading ? (
          <>{[...Array(2)].map((_, i) => <div key={i} className="h-16 bg-white/[0.04] rounded-xl animate-pulse" />)}</>
        ) : offers.length === 0 ? (
          <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-8 text-center">
            <Gift className="w-7 h-7 text-white/20 mx-auto mb-2" />
            <p className="text-white/55 text-sm font-medium">No offers yet. Create one to let people spend their points.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {offers.map((o) => (
              <div key={o.id} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-4 flex items-start gap-3 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${offerStatusColor[o.status] || offerStatusColor.draft}`}>{o.status}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold">{o.title}</p>
                  {o.description && <p className="text-white/65 text-xs mt-0.5">{o.description}</p>}
                  <p className="text-white/40 text-[11px] mt-1 font-mono">{o.pointCost} pts · {o.stock === null ? "unlimited" : `${o.stock} in stock`}</p>
                </div>
                <button onClick={() => toggleOffer(o)} className="text-white/70 hover:text-white text-xs px-2.5 py-1 rounded border border-white/15 hover:border-white/30">{o.status === "open" ? "Close" : "Open"}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-white/85 text-sm font-semibold flex items-center gap-2"><Award className="w-4 h-4 text-[#FF5C00]" /> Redemption requests</p>
          <div className="flex items-center gap-1 bg-white/[0.04] border border-white/[0.08] rounded-lg p-1">
            {(["requested", "fulfilled", "rejected"] as const).map((s) => (
              <button key={s} onClick={() => setReqStatus(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono rounded-md transition-colors ${reqStatus === s ? "bg-[#FF5C00] text-white" : "text-white/60 hover:text-white"}`}>{s}</button>
            ))}
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-8 text-center">
            <Award className="w-7 h-7 text-white/20 mx-auto mb-2" />
            <p className="text-white/55 text-sm font-medium">No {reqStatus} requests.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-4 flex items-start gap-3 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border ${reqStatusColor[r.status] || reqStatusColor.requested}`}>{r.status}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold">{r.offerTitle || `Offer #${r.offerId}`}</p>
                  <p className="text-white/40 text-[11px] mt-1 font-mono"><span className={kindColor[r.accountKind] || "text-white/50"}>{r.accountKind}</span> · {r.accountEmail} · {r.pointCostSnapshot} pts · {new Date(r.createdAt).toLocaleDateString()}</p>
                  {r.adminNote && <p className="text-white/55 text-xs mt-1 italic">{r.adminNote}</p>}
                </div>
                {r.status === "requested" && (
                  resolvingId === r.id ? (
                    <div className="w-44"><ProgressLoader active title="Resolving" stages={["Updating the request", "Adjusting points & stock"]} stageIntervalMs={900} /></div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => resolve(r.id, "fulfil")} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-2.5 py-1 rounded flex items-center gap-1"><Check className="w-3 h-3" /> Fulfil</button>
                      <button onClick={() => resolve(r.id, "reject")} className="bg-white/[0.06] hover:bg-white/[0.1] text-white/70 border border-white/15 text-xs px-2.5 py-1 rounded flex items-center gap-1"><X className="w-3 h-3" /> Reject</button>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OfferForm({ onCreated }: { onCreated: () => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [pointCost, setPointCost] = useState("100");
  const [stock, setStock] = useState("");
  const [status, setStatus] = useState<"draft" | "open" | "closed">("open");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const cost = parseInt(pointCost, 10);
    if (title.trim().length < 2) { setError("Title needs at least 2 characters."); return; }
    if (!Number.isInteger(cost) || cost <= 0) { setError("Point cost must be a positive whole number."); return; }
    const stockNum = stock.trim() === "" ? null : parseInt(stock, 10);
    if (stockNum !== null && (!Number.isInteger(stockNum) || stockNum < 0)) { setError("Stock must be blank (unlimited) or a non-negative whole number."); return; }
    setSaving(true);
    try {
      const r = await fetch(`${BASE}api/admin/redemption-offers`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ title: title.trim(), description: description.trim() || null, pointCost: cost, stock: stockNum, status }),
      });
      const j = await r.json().catch(() => null);
      if (r.ok) { setTitle(""); setDescription(""); setPointCost("100"); setStock(""); onCreated(); }
      else setError(j?.error || "Create failed.");
    } catch { setError("Network error."); }
    setSaving(false);
  };

  return (
    <div className="bg-white/[0.04] border border-[#FF5C00]/25 rounded-xl p-4 space-y-3">
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Reward title (e.g. 'Favcy hoodie')" className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is it? (optional)" rows={2} className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30" />
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-white/55 text-[10px] uppercase tracking-wider font-mono">Point cost</label>
          <input value={pointCost} onChange={(e) => setPointCost(e.target.value)} inputMode="numeric" className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white mt-1" />
        </div>
        <div>
          <label className="text-white/55 text-[10px] uppercase tracking-wider font-mono">Stock (blank = ∞)</label>
          <input value={stock} onChange={(e) => setStock(e.target.value)} inputMode="numeric" placeholder="∞" className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white mt-1" />
        </div>
        <div>
          <label className="text-white/55 text-[10px] uppercase tracking-wider font-mono">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as any)} className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white mt-1">
            <option value="open">Open</option>
            <option value="draft">Draft</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>
      {error && <p className="text-red-300 text-xs">{error}</p>}
      <button onClick={submit} disabled={saving} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create offer</button>
    </div>
  );
}
